import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { eatDayOf, formatIsoDay } from '@wodi/domain';
import type {
  ConsentTextView,
  DuplicateCheckResponse,
  OptionalRequiredField,
  SessionView,
  ValueSetConceptView,
} from '@wodi/shared';
import {
  Button,
  DateField,
  FieldGroup,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  PhoneField,
  RadioGroup,
  Select,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { ApiError } from '../api/client.ts';
import { duplicateCheck, registerPatient } from '../api/patients.ts';
import {
  consentTextsQuery,
  registrationSettingsQuery,
  valueSetQuery,
} from '../api/registration-config.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { draftStore, registrationDraftKey } from '../patients/drafts.ts';
import { DuplicateDialog } from '../patients/duplicate-dialog.tsx';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import styles from '../patients/patients.module.css';
import { takeRegistrationPrefill } from '../patients/prefill.ts';
import {
  duplicateCheckInput,
  emptyRegistration,
  IDENTIFIER_LABELS,
  prefillFromSearch,
  RECEPTION_IDENTIFIER_SYSTEMS,
  toRegisterRequest,
  validateRegistration,
  type RegistrationErrors,
  type RegistrationValues,
} from '../patients/registration-model.ts';
import { toasts } from '../toasts.ts';
import { screen } from '../layout/screen.tsx';

const DRAFT_SAVE_DELAY_MS = 400;

function todayEat(): string {
  return formatIsoDay(eatDayOf(new Date()));
}

function activeConcepts(concepts: readonly ValueSetConceptView[] | undefined) {
  return (concepts ?? [])
    .filter((c) => c.status === 'active')
    .toSorted((a, b) => a.sortOrder - b.sortOrder);
}

function activeConsentTexts(texts: readonly ConsentTextView[] | undefined) {
  return (texts ?? []).filter((t) => t.status === 'active');
}

const LANGUAGE_LABELS = { en: 'English', sw: 'Kiswahili' } as const;

type Blocking = DuplicateCheckResponse['blocking'];
type Candidates = DuplicateCheckResponse['candidates'];

/**
 * Register a new patient (plan §6.4). Single column, max 720px. The draft saves to this
 * device as it is typed; the duplicate check runs before registering and when an ID or
 * phone loses focus.
 */
export function RegisterPatientPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'patients.register')) {
    return (
      <>
        <PageHeader title="Register new patient" />
        <Notice tone="info">
          You don't have permission to register patients. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return <RegistrationLoader session={session} />;
}

function RegistrationLoader({ session }: { session: SessionView }) {
  const sex = useQuery(valueSetQuery('patient_sex'));
  const relationship = useQuery(valueSetQuery('relationship'));
  const consent = useQuery(consentTextsQuery);
  const settings = useQuery(registrationSettingsQuery);
  const queries = [sex, relationship, consent, settings];
  const draftKey = registrationDraftKey(session.user.id, session.facility.id);
  const [draft, setDraft] = useState<{ loaded: boolean; values: RegistrationValues | null }>({
    loaded: false,
    values: null,
  });

  useEffect(() => {
    let cancelled = false;
    void draftStore()
      .get(draftKey)
      .catch(() => undefined)
      .then((stored) => {
        if (cancelled) return;
        setDraft({ loaded: true, values: (stored as RegistrationValues | undefined) ?? null });
      });
    return () => {
      cancelled = true;
    };
  }, [draftKey]);

  const failed = queries.find((q) => q.isError);
  return (
    <>
      <PageHeader title="Register new patient" />
      {failed?.isError === true ? (
        <Notice
          tone="error"
          action={
            <Button
              dense
              onPress={() => {
                for (const q of queries) if (q.isError) void q.refetch();
              }}
            >
              Try again
            </Button>
          }
        >
          {failed.error instanceof ApiError
            ? failed.error.message
            : "The registration form couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {failed === undefined && (queries.some((q) => q.isPending) || !draft.loaded) ? (
        <LoadingBlock label="Loading the registration form" rows={6} />
      ) : null}
      {sex.isSuccess &&
      relationship.isSuccess &&
      consent.isSuccess &&
      settings.isSuccess &&
      draft.loaded ? (
        <RegistrationForm
          draftKey={draftKey}
          restored={draft.values}
          sexOptions={activeConcepts(sex.data)}
          relationshipOptions={activeConcepts(relationship.data)}
          consentTexts={activeConsentTexts(consent.data)}
          requiredFields={settings.data.requiredFields}
        />
      ) : null}
    </>
  );
}

function RegistrationForm({
  draftKey,
  restored,
  sexOptions,
  relationshipOptions,
  consentTexts,
  requiredFields,
}: {
  draftKey: string;
  restored: RegistrationValues | null;
  sexOptions: ValueSetConceptView[];
  relationshipOptions: ValueSetConceptView[];
  consentTexts: ConsentTextView[];
  requiredFields: readonly OptionalRequiredField[];
}) {
  const navigate = useNavigate();
  const online = useOnline();
  const [initial] = useState<RegistrationValues>(() => {
    if (restored !== null) return restored;
    const empty = emptyRegistration(crypto.randomUUID());
    const prefill = takeRegistrationPrefill();
    const withText = prefill === null ? empty : prefillFromSearch(empty, prefill);
    const firstText = consentTexts.find((t) => t.language === 'en') ?? consentTexts[0];
    return { ...withText, consentTextId: firstText?.id ?? '' };
  });
  const form = useForm<RegistrationValues>({ defaultValues: initial });
  const identifiers = useFieldArray({ control: form.control, name: 'identifiers' });
  const values = useWatch({ control: form.control }) as RegistrationValues;
  const [submitted, setSubmitted] = useState(false);
  const [saved, setSaved] = useState(restored !== null);
  const [blocking, setBlocking] = useState<Blocking>([]);
  const [candidates, setCandidates] = useState<Candidates>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dependantSearch, setDependantSearch] = useState('');
  const store = draftStore();
  const discarded = useRef(false);

  const ctx = { requiredFields, consentAvailable: consentTexts.length > 0, today: todayEat() };
  const errors: RegistrationErrors = submitted ? validateRegistration(values, ctx) : {};
  const err = (key: string) => errors[key];

  // Save the draft to this device after each pause in typing.
  // Nothing is saved until something is typed.
  const serialised = JSON.stringify(values);
  const touched = form.formState.isDirty || restored !== null;
  useEffect(() => {
    if (!store.available || !touched) return undefined;
    const timer = window.setTimeout(() => {
      if (discarded.current) return;
      void store
        .set(draftKey, JSON.parse(serialised) as RegistrationValues)
        .then(() => {
          setSaved(true);
        })
        .catch(() => {
          setSaved(false);
        });
    }, DRAFT_SAVE_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [serialised, draftKey, store, touched]);

  const finish = async (to: { patientId: string }) => {
    discarded.current = true;
    await store.delete(draftKey).catch(() => undefined);
    await navigate({ to: '/reception', search: { patientId: to.patientId } });
  };

  const register = useMutation({
    mutationFn: (override: { candidateIds: string[]; reason: string } | null) =>
      registerPatient(
        toRegisterRequest(values, { sex: sexOptions, relationship: relationshipOptions }, override),
      ),
    onSuccess: async (patient) => {
      setDialogOpen(false);
      toasts.show({ title: 'Patient registered', description: patient.registrySync.message });
      await finish({ patientId: patient.id });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === 'duplicate_candidates') {
        await runDuplicateCheck(true);
      }
    },
  });

  const check = useMutation({
    mutationFn: () => duplicateCheck(duplicateCheckInput(values)),
  });

  async function runDuplicateCheck(openDialog: boolean): Promise<DuplicateCheckResponse | null> {
    try {
      const result = await check.mutateAsync();
      setBlocking(result.blocking);
      setCandidates(result.candidates);
      if (openDialog && result.blocking.length === 0 && result.candidates.length > 0) {
        setDialogOpen(true);
      }
      return result;
    } catch {
      return null;
    }
  }

  const onBlurCheck = () => {
    if (!online || values.given.trim() === '') return;
    const hasKey = values.phone !== null || values.identifiers.some((i) => i.value.trim() !== '');
    if (hasKey) void runDuplicateCheck(false);
  };

  const submit = async () => {
    setSubmitted(true);
    register.reset();
    if (Object.keys(validateRegistration(values, ctx)).length > 0) return;
    const result = await runDuplicateCheck(true);
    if (result === null || result.blocking.length > 0 || result.candidates.length > 0) return;
    register.mutate(null);
  };
  useShortcut(
    'mod+enter',
    () => {
      if (online && !register.isPending && !check.isPending) void submit();
    },
    { allowInFields: true, enabled: !dialogOpen, description: 'Register patient' },
  );

  const discard = async () => {
    discarded.current = true;
    await store.delete(draftKey).catch(() => undefined);
    discarded.current = false;
    setSubmitted(false);
    setBlocking([]);
    setCandidates([]);
    setSaved(false);
    form.reset({
      ...emptyRegistration(crypto.randomUUID()),
      consentTextId: values.consentTextId,
    });
  };

  const set = <K extends keyof RegistrationValues>(key: K, value: RegistrationValues[K]) => {
    form.setValue(key as never, value as never, { shouldDirty: true });
  };

  const sexProvisional = sexOptions.some((c) => c.provisional);
  const chosenConsent = consentTexts.find((t) => t.id === values.consentTextId) ?? consentTexts[0];
  const registerError = register.error;
  const checkError = check.error;

  return (
    <section className={screen.panel} aria-label="Patient details">
      <form
        className={styles.registration}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className={`${styles.actions} ${styles.draftBar}`}>
          <span className={styles.draftStatus} role="status">
            {store.available && saved ? 'Saved on this device' : ''}
          </span>
          {restored !== null || saved ? (
            <Button variant="quiet" onPress={() => void discard()}>
              Discard draft
            </Button>
          ) : null}
        </div>

        <div className={styles.registrationGroups}>
          <FieldGroup legend="Name">
            <div className={styles.row}>
              <TextField
                label="First name"
                autoFocus
                value={values.given}
                onChange={(v) => {
                  set('given', v);
                }}
                {...(err('given') === undefined ? {} : { errorMessage: err('given') })}
              />
              <TextField
                label="Middle name"
                value={values.middle}
                onChange={(v) => {
                  set('middle', v);
                }}
              />
              <TextField
                label="Family name"
                value={values.family}
                onChange={(v) => {
                  set('family', v);
                }}
                {...(err('family') === undefined ? {} : { errorMessage: err('family') })}
              />
            </div>
          </FieldGroup>

          <FieldGroup legend="Sex and date of birth">
            {sexOptions.length === 0 ? (
              <Notice tone="error">
                No sex values are set up, so patients can't be registered yet. An administrator can
                add them in Settings.
              </Notice>
            ) : (
              <RadioGroup
                label="Sex"
                orientation="horizontal"
                value={values.sexId}
                onChange={(v) => {
                  set('sexId', v);
                }}
                options={sexOptions.map((c) => ({ id: c.id, label: c.display }))}
                {...(sexProvisional
                  ? {
                      description: 'This list is provisional until the national list is confirmed.',
                    }
                  : {})}
                {...(err('sexId') === undefined ? {} : { errorMessage: err('sexId') })}
              />
            )}
            <RadioGroup
              label="Date of birth"
              orientation="horizontal"
              value={values.birthKind}
              onChange={(v) => {
                set('birthKind', v as RegistrationValues['birthKind']);
              }}
              options={[
                { id: 'exact', label: 'Date of birth known' },
                { id: 'estimated', label: 'Estimated age' },
              ]}
            />
            {values.birthKind === 'exact' ? (
              <DateField
                label="Date of birth"
                value={values.birthDate}
                onChange={(v) => {
                  set('birthDate', v);
                }}
                maxValue={ctx.today}
                description="Day, month, year."
                {...(err('birthDate') === undefined ? {} : { errorMessage: err('birthDate') })}
              />
            ) : (
              <div className={styles.row}>
                <TextField
                  label="Estimated age"
                  inputMode="numeric"
                  value={values.ageValue}
                  onChange={(v) => {
                    set('ageValue', v);
                  }}
                  description="Years, or months for a child under 2."
                  {...(err('ageValue') === undefined ? {} : { errorMessage: err('ageValue') })}
                />
                <RadioGroup
                  label="Age in"
                  orientation="horizontal"
                  value={values.ageUnit}
                  onChange={(v) => {
                    set('ageUnit', v as RegistrationValues['ageUnit']);
                  }}
                  options={[
                    { id: 'years', label: 'Years' },
                    { id: 'months', label: 'Months' },
                  ]}
                />
              </div>
            )}
          </FieldGroup>

          <FieldGroup
            legend="Identifiers"
            description="ID documents the patient has with them. Numbers are checked against patients already registered."
            {...(err('identifiers') === undefined ? {} : { errorMessage: err('identifiers') })}
          >
            {identifiers.fields.map((field, index) => {
              const row = values.identifiers[index];
              const passport = row?.system === 'passport';
              const key = (name: string) => `identifiers.${String(index)}.${name}`;
              return (
                <div
                  key={field.id}
                  className={styles.identifierRow}
                  data-passport={passport || undefined}
                >
                  <Select
                    label="Type of ID"
                    placeholder="Choose a type"
                    options={RECEPTION_IDENTIFIER_SYSTEMS.map((s) => ({
                      id: s,
                      label: IDENTIFIER_LABELS[s],
                    }))}
                    value={row?.system === '' || row === undefined ? null : row.system}
                    onChange={(v) => {
                      form.setValue(`identifiers.${index}.system`, v as never, {
                        shouldDirty: true,
                      });
                    }}
                    {...(err(key('system')) === undefined
                      ? {}
                      : { errorMessage: err(key('system')) })}
                  />
                  <TextField
                    label="Number"
                    value={row?.value ?? ''}
                    onChange={(v) => {
                      form.setValue(`identifiers.${index}.value`, v, { shouldDirty: true });
                    }}
                    onBlur={onBlurCheck}
                    {...(err(key('value')) === undefined
                      ? {}
                      : { errorMessage: err(key('value')) })}
                  />
                  {passport ? (
                    <TextField
                      label="Country"
                      value={row.issuingCountry}
                      onChange={(v) => {
                        form.setValue(`identifiers.${index}.issuingCountry`, v, {
                          shouldDirty: true,
                        });
                      }}
                      {...(err(key('issuingCountry')) === undefined
                        ? {}
                        : { errorMessage: err(key('issuingCountry')) })}
                    />
                  ) : null}
                  <Button
                    variant="quiet"
                    aria-label={`Remove ID ${String(index + 1)}`}
                    onPress={() => {
                      identifiers.remove(index);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
            <div>
              <Button
                onPress={() => {
                  identifiers.append({ system: '', value: '', issuingCountry: '' });
                }}
              >
                Add ID
              </Button>
            </div>
            {blocking.map((conflict) => (
              <Notice
                key={`${conflict.patient.id}-${conflict.system}`}
                tone="error"
                action={
                  <Button dense onPress={() => void finish({ patientId: conflict.patient.id })}>
                    Use this patient
                  </Button>
                }
              >
                {`This ${IDENTIFIER_LABELS[conflict.system]} is already registered to ${conflict.patient.displayName}, ${conflict.patient.patientNumber}. Use that patient, or correct the number. If it is a parent's ID, register the child as their dependant below.`}
              </Notice>
            ))}
          </FieldGroup>

          <FieldGroup legend="Phone">
            <PhoneField
              label="Mobile number"
              value={values.phone}
              onChange={(v, code) => {
                set('phone', v);
                set('phoneError', code);
              }}
              onBlur={onBlurCheck}
              isRequired={requiredFields.includes('phone')}
              {...(err('phone') === undefined ? {} : { errorMessage: err('phone') })}
            />

            <RadioGroup
              label="Whose phone is this?"
              orientation="horizontal"
              value={values.phoneOwner}
              onChange={(v) => {
                set('phoneOwner', v as RegistrationValues['phoneOwner']);
              }}
              options={[
                { id: 'patient', label: "The patient's" },
                { id: 'someone_else', label: "Someone else's, like a parent's" },
              ]}
            />
          </FieldGroup>

          <FieldGroup
            legend="Residence"
            description="Free text until the county, sub-county and ward list is confirmed."
          >
            <div className={styles.row}>
              <TextField
                label="County"
                value={values.county}
                onChange={(v) => {
                  set('county', v);
                }}
                {...(err('county') === undefined ? {} : { errorMessage: err('county') })}
              />
              <TextField
                label="Sub-county"
                value={values.subCounty}
                onChange={(v) => {
                  set('subCounty', v);
                }}
              />
              <TextField
                label="Ward"
                value={values.ward}
                onChange={(v) => {
                  set('ward', v);
                }}
              />
            </div>
            <TextField
              label="Village or estate"
              value={values.locality}
              onChange={(v) => {
                set('locality', v);
              }}
            />
          </FieldGroup>

          <FieldGroup legend="Next of kin" description="Someone to contact about the patient.">
            <div className={styles.row}>
              <TextField
                label="Next of kin's first name"
                value={values.kinGiven}
                onChange={(v) => {
                  set('kinGiven', v);
                }}
                {...(err('kinGiven') === undefined ? {} : { errorMessage: err('kinGiven') })}
              />
              <TextField
                label="Next of kin's family name"
                value={values.kinFamily}
                onChange={(v) => {
                  set('kinFamily', v);
                }}
              />
            </div>
            <div className={styles.row}>
              <Select
                label="Relationship to the patient"
                width="md"
                placeholder="Choose a relationship"
                options={relationshipOptions.map((c) => ({ id: c.id, label: c.display }))}
                value={values.kinRelationshipId === '' ? null : values.kinRelationshipId}
                onChange={(v) => {
                  set('kinRelationshipId', typeof v === 'string' ? v : '');
                }}
                {...(err('kinRelationshipId') === undefined
                  ? {}
                  : { errorMessage: err('kinRelationshipId') })}
              />
              <PhoneField
                label="Next of kin's mobile number"
                value={values.kinPhone}
                onChange={(v, code) => {
                  set('kinPhone', v);
                  set('kinPhoneError', code);
                }}
                {...(err('kinPhone') === undefined ? {} : { errorMessage: err('kinPhone') })}
              />
            </div>
          </FieldGroup>

          <FieldGroup
            legend="Dependant of"
            description="For a child or other dependant: the patient they depend on."
          >
            {values.dependantOf === null ? (
              <PatientSearchPicker
                inputValue={dependantSearch}
                onInputChange={setDependantSearch}
                onSelect={(result) => {
                  set('dependantOf', {
                    id: result.id,
                    displayName: result.displayName,
                    patientNumber: result.patientNumber,
                  });
                  setDependantSearch('');
                }}
              />
            ) : (
              <>
                <div className={styles.chosen}>
                  <span>
                    {values.dependantOf.displayName}{' '}
                    <span className="wodi-id">{values.dependantOf.patientNumber}</span>
                  </span>
                  <Button
                    variant="quiet"
                    onPress={() => {
                      set('dependantOf', null);
                      set('dependantRelationshipId', '');
                    }}
                  >
                    Change
                  </Button>
                </div>
                <Select
                  label="Patient's relationship to them"
                  placeholder="Choose a relationship"
                  options={relationshipOptions.map((c) => ({ id: c.id, label: c.display }))}
                  value={
                    values.dependantRelationshipId === '' ? null : values.dependantRelationshipId
                  }
                  onChange={(v) => {
                    set('dependantRelationshipId', typeof v === 'string' ? v : '');
                  }}
                  {...(err('dependantRelationshipId') === undefined
                    ? {}
                    : { errorMessage: err('dependantRelationshipId') })}
                />
              </>
            )}
          </FieldGroup>

          <FieldGroup legend="Data-sharing consent">
            {chosenConsent === undefined ? (
              <Notice tone="info">
                The consent wording isn't set up yet, so consent can't be recorded. The patient is
                registered here, and the national registry link waits until consent is recorded.
              </Notice>
            ) : (
              <>
                {consentTexts.length > 1 ? (
                  <RadioGroup
                    label="Language read to the patient"
                    orientation="horizontal"
                    value={chosenConsent.id}
                    onChange={(v) => {
                      set('consentTextId', v);
                    }}
                    options={consentTexts.map((t) => ({
                      id: t.id,
                      label: LANGUAGE_LABELS[t.language],
                    }))}
                  />
                ) : null}
                <p className={styles.consentText}>{chosenConsent.body}</p>
                <RadioGroup
                  label="The patient's decision"
                  description="Read the wording to the patient. Recorded as verbal consent, taken by you."
                  value={values.consentDecision}
                  onChange={(v) => {
                    set('consentDecision', v as RegistrationValues['consentDecision']);
                    if (values.consentTextId === '') set('consentTextId', chosenConsent.id);
                  }}
                  options={[
                    { id: 'granted', label: 'Agreed to share' },
                    { id: 'refused', label: 'Refused' },
                    { id: 'not_asked', label: 'Not asked yet' },
                  ]}
                  {...(err('consentDecision') === undefined
                    ? {}
                    : { errorMessage: err('consentDecision') })}
                />
              </>
            )}
          </FieldGroup>
        </div>

        {submitted && Object.keys(errors).length > 0 ? (
          <Notice tone="error">Some details need attention. Check the messages above.</Notice>
        ) : null}
        {checkError === null ? null : (
          <Notice tone="error">
            {checkError instanceof ApiError
              ? checkError.message
              : "The duplicate check didn't run. Try again."}
          </Notice>
        )}
        {registerError === null ||
        (registerError instanceof ApiError &&
          registerError.code === 'duplicate_candidates') ? null : (
          <Notice tone="error">
            {registerError instanceof ApiError
              ? registerError.message
              : "The patient couldn't be registered. Try again."}
          </Notice>
        )}
        {online ? null : (
          <Notice tone="info">
            Registering needs a connection to Wodi. Your draft is saved on this device.
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={register.isPending || check.isPending}
            pendingLabel="Registering…"
          >
            Register patient
          </Button>
          <Kbd combo="mod+enter" />
        </div>
        <DuplicateDialog
          candidates={candidates}
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          isPending={register.isPending}
          onUse={(patientId) => {
            setDialogOpen(false);
            void finish({ patientId });
          }}
          onDifferent={(reason) => {
            register.mutate({ candidateIds: candidates.map((c) => c.patient.id), reason });
          }}
        />
        {candidates.length > 0 && !dialogOpen ? (
          <Notice
            tone="info"
            action={
              <Button
                dense
                onPress={() => {
                  setDialogOpen(true);
                }}
              >
                Review possible matches
              </Button>
            }
          >
            {`${String(candidates.length)} possible ${candidates.length === 1 ? 'match' : 'matches'} found.`}
          </Notice>
        ) : null}
      </form>
    </section>
  );
}
